import React from "react";
import useBackOfficeSync from "@/hooks/useBackOfficeSync";
import { CloudOff, RefreshCw, CheckCircle2 } from "lucide-react";

/**
 * BackOfficeSyncPill — shows pending back-office docs (GRN, Stock Entry,
 * Wastage, Transfers) queued in IndexedDB while offline. Click to force
 * a flush.
 *
 * Colors:
 *   • pending > 0        — amber "N queued"
 *   • pending 0, offline — grey  "Offline"
 *   • pending 0, online  — hidden (nothing to show)
 */
export default function BackOfficeSyncPill() {
  const { online, pending, flushing, flushNow } = useBackOfficeSync();

  if (pending === 0 && online) return null;

  const isOffline = !online;
  const bg = pending > 0 ? "bg-[#FDF3E1]" : "bg-[#F0EEEA]";
  const border = pending > 0 ? "border-[#E8C88A]" : "border-[#D8D5D0]";
  const text = pending > 0 ? "text-[#8A6240]" : "text-[#5C5A58]";
  const dot = pending > 0 ? "bg-[#B87B41]" : "bg-[#8A8782]";

  return (
    <button
      type="button"
      onClick={flushNow}
      disabled={flushing || (!online && pending > 0)}
      title={
        pending > 0
          ? `${pending} back-office doc${pending > 1 ? "s" : ""} pending sync — click to flush now`
          : "Browser is offline"
      }
      className={`inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1.5 rounded-full border ${bg} ${border} ${text} transition`}
      data-testid="bo-sync-pill"
    >
      <span className={`w-1.5 h-1.5 rounded-full ${dot} ${flushing ? "animate-pulse" : ""}`} />
      {isOffline ? (
        <>
          <CloudOff size={12} />
          {pending > 0 ? `Offline · ${pending} queued` : "Offline"}
        </>
      ) : pending > 0 ? (
        <>
          {flushing ? "Syncing…" : `${pending} queued`}
          <RefreshCw size={11} className={flushing ? "animate-spin" : ""} />
        </>
      ) : (
        <>
          <CheckCircle2 size={12} /> Synced
        </>
      )}
    </button>
  );
}
